/**
 * Auth Events Module
 *
 * Provides a way to dispatch auth-related events from outside React components.
 * Used by the query client to trigger logout when session expires.
 */

type AuthEventType = "session-expired" | "force-logout";

interface AuthEventDetail {
  reason?: string;
  status?: number;
}

type AuthEventHandler = (detail: AuthEventDetail) => void;

class AuthEventEmitter {
  private handlers: Map<AuthEventType, Set<AuthEventHandler>> = new Map();
  private isProcessing = false;

  /**
   * Subscribe to an auth event
   */
  on(event: AuthEventType, handler: AuthEventHandler): () => void {
    if (!this.handlers.has(event)) {
      this.handlers.set(event, new Set());
    }
    this.handlers.get(event)!.add(handler);

    // Return unsubscribe function
    return () => {
      this.handlers.get(event)?.delete(handler);
    };
  }

  /**
   * Emit an auth event
   * Uses debouncing to prevent multiple rapid calls (e.g., multiple 401s)
   */
  emit(event: AuthEventType, detail: AuthEventDetail = {}): void {
    // Prevent multiple simultaneous session-expired events
    if (event === "session-expired" && this.isProcessing) {
      console.debug("[AuthEvents] Ignoring duplicate session-expired event");
      return;
    }

    if (event === "session-expired") {
      this.isProcessing = true;
      // Reset after a short delay to allow new events
      setTimeout(() => {
        this.isProcessing = false;
      }, 2000);
    }

    console.log(`[AuthEvents] Emitting ${event}:`, detail);

    const handlers = this.handlers.get(event);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(detail);
        } catch (error) {
          console.error(`[AuthEvents] Handler error for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Clear all handlers (useful for testing)
   */
  clear(): void {
    this.handlers.clear();
    this.isProcessing = false;
  }
}

// Singleton instance
export const authEvents = new AuthEventEmitter();

let sessionExpiredFired = false;

/**
 * Emit session expired event (convenience function)
 */
export function emitSessionExpired(reason?: string, status?: number): void {
  if (sessionExpiredFired) return;
  sessionExpiredFired = true;
  authEvents.emit("session-expired", { reason, status });
}

/**
 * Emit force logout event (convenience function)
 */
export function emitForceLogout(reason?: string): void {
  authEvents.emit("force-logout", { reason });
}
